import { SpecialtyType } from "../../enums/SpecialtyType";

type AuthConfig = Readonly<{
  auth?: {
    logoutUrl: string;
  };
}>;

/**
 * Returns the post-login redirect URI for B2C.
 * If the path starts with '/specialty/', returns the current path.
 * Otherwise, returns '/'.
 */
export const getB2CPostLoginRedirectUri = (): string => {
  const path = window.location.pathname;
  return path.startsWith("/specialty/") ? path : "/";
};

/**
 * Constructs the B2C logout URL for a given specialty type.
 * @param configData - Auth configuration object containing the base logout URL.
 * @param specialtyTypeId - The specialty type ID.
 * @returns The constructed logout URL.
 */
export const getB2CLogoutUrl = (
  configData: AuthConfig,
  specialtyTypeId: number,
): string => {
  const configLogoutUrl = configData?.auth?.logoutUrl ?? "";
  const currentBaseUrl = window.location.origin;
  const postfix = getLogoutPostfix(specialtyTypeId);
  const redirectUrl = `${currentBaseUrl}/logout/${postfix}`;

  return configLogoutUrl.replace(currentBaseUrl, redirectUrl);
};

/**
 * Returns the specialty postfix path for a given specialty type.
 * Used for both login and logout URL construction.
 */
const getSpecialtyAuthPostfix = (specialtyType: number): string => {
  switch (specialtyType) {
    case SpecialtyType.OPHTHALMOLOGY:
      return "retina"; // don't change as already configured
    case SpecialtyType.ONCOLOGY_HEMATOLOGY:
      return "oncology"; // don't change as already configured
    case SpecialtyType.RHEUMATOLOGY:
      return "rheumatology";
    case SpecialtyType.UROLOGY:
      return "urology";
    case SpecialtyType.GASTROENTEROLOGY:
      return "gastroenterology";
    case SpecialtyType.NEUROLOGY:
      return "neurology";
    case SpecialtyType.GYNECOLOGY:
      return "gynecology";
    case SpecialtyType.PULMONOLOGY:
      return "pulmonology";
    case SpecialtyType.NEPHROLOGY:
      return "nephrology";
    case SpecialtyType.IMMUNOLOGY:
      return "immunology";
    default:
      return "default";
  }
};

/**
 * Returns the login postfix path for a given specialty type.
 * This is only used for login URL construction.
 */
export const getLoginPostfix = getSpecialtyAuthPostfix;

/**
 * Returns the logout postfix path for a given specialty type.
 * This is only used for logout URL construction.
 */
export const getLogoutPostfix = getSpecialtyAuthPostfix;
